/**
 * Trust Centre (policy BUILD-10) — the public /trust page.
 *
 * Everything on this page comes from the API. Nothing is hard-coded here: the
 * subprocessor list, the security claims and their evidence, the disclosure
 * policy, the status-page link and the DPA link are all server-resolved, so
 * publishing a change is an operational act rather than a frontend deploy.
 *
 * THE RULE THAT SHAPES THIS FILE
 * A section renders only when `sections.<name>` is true. There is no
 * "coming soon", no empty heading, no placeholder. A trust page that advertises
 * assurance it does not have is worse than one that stays quiet, so a section
 * that is not ready is simply not on the page.
 *
 * The one deliberate exception is incident history: "no incidents have been
 * published" is a real answer to a real question, so that section renders its
 * empty state rather than disappearing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getTrustOverview,
  subscribeToSubprocessorNotices,
  type Subprocessor,
  type TrustIncident,
  type TrustOverview,
} from '@/api/trust'

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toISOString().slice(0, 10)
}

function Section({
  id,
  title,
  description,
  children,
}: {
  id: string
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="mt-12">
      <h2 id={`${id}-heading`} className="mb-2 text-xl font-semibold text-foreground">
        {title}
      </h2>
      {description ? <p className="mb-4 text-muted-foreground">{description}</p> : null}
      {children}
    </section>
  )
}

function SubprocessorTable({ rows }: { rows: Subprocessor[] }) {
  if (rows.length === 0) {
    return <p className="text-muted-foreground">No subprocessors are currently disclosed.</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">
          Third parties that process customer data, their purpose, the categories of data they
          handle, where they process it, and the date they became or become active.
        </caption>
        <thead>
          <tr className="border-b border-border">
            <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Processor</th>
            <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Purpose</th>
            <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Data categories</th>
            <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Location</th>
            <th scope="col" className="py-2 font-semibold text-foreground">Active from</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-border/60 align-top">
              <td className="py-2 pr-4 font-medium text-foreground">
                {row.name}
                {row.pending ? (
                  <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                    announced
                  </span>
                ) : null}
              </td>
              <td className="py-2 pr-4 text-muted-foreground">{row.purpose}</td>
              <td className="py-2 pr-4 text-muted-foreground">
                {row.data_categories.length ? row.data_categories.join(', ') : '—'}
              </td>
              <td className="py-2 pr-4 text-muted-foreground">{row.location}</td>
              <td className="py-2 text-muted-foreground">{formatDate(row.added_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function NoticeSubscribeForm() {
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [message, setMessage] = useState('')

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      setState('sending')
      try {
        const res = await subscribeToSubprocessorNotices(email)
        setState('sent')
        setMessage(res.message)
        setEmail('')
      } catch (err) {
        setState('error')
        setMessage((err as Error).message || 'Could not sign you up. Please try again.')
      }
    },
    [email],
  )

  return (
    <form onSubmit={submit} className="mt-4 max-w-md">
      <label htmlFor="trust-notice-email" className="mb-1 block text-sm font-medium text-foreground">
        Email address
      </label>
      <div className="flex gap-2">
        <input
          id="trust-notice-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
          aria-describedby="trust-notice-help"
        />
        <button
          type="submit"
          disabled={state === 'sending'}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {state === 'sending' ? 'Signing up…' : 'Notify me'}
        </button>
      </div>
      <p id="trust-notice-help" className="mt-2 text-xs text-muted-foreground">
        We will send one confirmation email. Notices are sent at least 30 days before a new
        subprocessor starts handling your data. You can unsubscribe from any notice.
      </p>
      {/* role=status so a screen reader announces the outcome without stealing focus. */}
      <p role="status" aria-live="polite" className="mt-2 text-sm text-muted-foreground">
        {state === 'sent' || state === 'error' ? message : ''}
      </p>
    </form>
  )
}

function IncidentList({ incidents }: { incidents: TrustIncident[] }) {
  if (incidents.length === 0) {
    return (
      <p className="text-muted-foreground">
        No incidents have been published. Incidents affecting the confidentiality, integrity or
        availability of customer data are published here once they are resolved and reviewed.
      </p>
    )
  }
  return (
    <ul className="space-y-4">
      {incidents.map((incident) => (
        <li key={incident.id} className="border-b border-border/60 pb-4">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <h3 className="font-medium text-foreground">{incident.title}</h3>
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              {incident.severity} · {incident.status}
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{incident.summary}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Started {formatDate(incident.started_at)}
            {incident.resolved_at ? ` · Resolved ${formatDate(incident.resolved_at)}` : ''}
          </p>
        </li>
      ))}
    </ul>
  )
}

export function TrustCentrePage() {
  const [data, setData] = useState<TrustOverview | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getTrustOverview()
      .then((overview) => {
        if (!cancelled) setData(overview)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || 'Could not load the Trust Centre.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const sections = data?.sections
  const practices = useMemo(() => data?.security_practices ?? [], [data])

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="mb-4 text-3xl font-bold tracking-tight">Trust Centre</h1>
      <p className="max-w-2xl text-muted-foreground leading-relaxed">
        How WingCaster handles your data, who else processes it, and how to reach us about
        security. See also our{' '}
        <Link to="/privacy" className="underline underline-offset-4">Privacy Policy</Link>,{' '}
        <Link to="/terms" className="underline underline-offset-4">Terms of Service</Link> and{' '}
        <Link to="/cookies" className="underline underline-offset-4">Cookie Notice</Link>.
      </p>

      {error ? (
        <p role="alert" className="mt-8 text-muted-foreground">
          {error}
        </p>
      ) : null}

      {!data && !error ? (
        <p className="mt-8 text-muted-foreground">Loading…</p>
      ) : null}

      {data ? (
        <>
          {/* Item 3 — someone else's status page. We do not compute uptime here. */}
          {sections?.status && data.status_page_url ? (
            <Section
              id="status"
              title="Service status"
              description="Live availability is published on our status page, which is hosted independently of the platform it reports on."
            >
              <a
                href={data.status_page_url}
                target="_blank"
                rel="noreferrer noopener"
                className="underline underline-offset-4"
              >
                View the status page
              </a>
            </Section>
          ) : null}

          {/* Item 1 — the register. */}
          <Section
            id="subprocessors"
            title="Subprocessors"
            description="Third parties that process customer data on our behalf. Processors marked “announced” have been disclosed in advance and are not yet handling data."
          >
            <SubprocessorTable rows={data.subprocessors} />
          </Section>

          {/* Item 2 — double opt-in subscription to advance notices. */}
          <Section
            id="subprocessor-notices"
            title="Get notified of changes"
            description="We give at least 30 days' notice before a new subprocessor starts handling your data, so you have time to object."
          >
            <NoticeSubscribeForm />
          </Section>

          {/* Item 5 — rendered only when residency data exists (BUILD-6). */}
          {sections?.residency && data.residency?.length ? (
            <Section
              id="residency"
              title="Data residency"
              description="Where data for each market is stored and processed, and the basis for any transfer out of it."
            >
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left text-sm">
                  <caption className="sr-only">Data residency and transfer basis per market.</caption>
                  <thead>
                    <tr className="border-b border-border">
                      <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Market</th>
                      <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Primary region</th>
                      <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Backup region</th>
                      <th scope="col" className="py-2 pr-4 font-semibold text-foreground">Transfers out</th>
                      <th scope="col" className="py-2 font-semibold text-foreground">Mechanism</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.residency.map((row) => (
                      <tr key={row.market} className="border-b border-border/60">
                        <td className="py-2 pr-4 font-medium text-foreground">{row.market_name}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{row.primary_region ?? '—'}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{row.backup_region ?? '—'}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{row.cross_border_transfer ?? '—'}</td>
                        <td className="py-2 text-muted-foreground">{row.transfer_mechanism ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          ) : null}

          {/* Item 6 — every claim shows what evidences it. */}
          <Section
            id="security-practices"
            title="Security practices"
            description="Each statement below names the assessment finding, test, or shipped change that substantiates it. Controls we have not built yet are not listed."
          >
            <div className="space-y-8">
              {practices.map((group) => (
                <div key={group.category}>
                  <h3 className="mb-3 font-semibold text-foreground">{group.category}</h3>
                  <ul className="space-y-3">
                    {group.claims.map((claim) => (
                      <li key={claim.id} id={`claim-${claim.id}`}>
                        <p className="text-muted-foreground">{claim.claim}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          <span className="font-medium">Evidence:</span> {claim.evidence}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>

          {/* Item 7 — withheld until the open High findings are closed. */}
          {sections?.assessments && data.assessments?.length ? (
            <Section
              id="assessments"
              title="Assessments"
              description="Summaries of independent and internal assessments of our security and data-protection posture."
            >
              <div className="space-y-8">
                {data.assessments.map((assessment) => (
                  <article key={assessment.id}>
                    <h3 className="font-semibold text-foreground">
                      {assessment.title} <span className="font-normal text-muted-foreground">({assessment.period})</span>
                    </h3>
                    <p className="mt-2 text-muted-foreground">{assessment.scope}</p>
                    <p className="mt-2 text-muted-foreground leading-relaxed">{assessment.summary}</p>
                    <h4 className="mt-4 text-sm font-semibold text-foreground">Benchmarks</h4>
                    <ul className="mt-1 list-inside list-disc text-sm text-muted-foreground">
                      {assessment.benchmarks.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                    <p className="mt-3 text-sm text-muted-foreground">{assessment.remediation_position}</p>
                  </article>
                ))}
              </div>
            </Section>
          ) : null}

          {/* Item 8 — DPA. The download link appears only once published. */}
          <Section
            id="dpa"
            title="Data Processing Agreement"
            description="Our DPA governs our processing of personal data on your behalf."
          >
            {sections?.dpa_download && data.dpa.url ? (
              <p>
                <a href={data.dpa.url} className="underline underline-offset-4">
                  Download the current DPA
                </a>
                {data.dpa.version ? (
                  <span className="text-muted-foreground"> ({data.dpa.version})</span>
                ) : null}
              </p>
            ) : null}
            <p className={sections?.dpa_download ? 'mt-2 text-muted-foreground' : 'text-muted-foreground'}>
              To request a countersigned copy, email{' '}
              <a href={`mailto:${data.dpa.request_email}`} className="underline underline-offset-4">
                {data.dpa.request_email}
              </a>{' '}
              with your organisation's legal name and address.
            </p>
          </Section>

          {/* Item 8 — vulnerability disclosure. */}
          <Section
            id="vulnerability-disclosure"
            title="Reporting a vulnerability"
            description="If you have found a security issue, we want to hear about it."
          >
            <p className="text-muted-foreground">
              Email{' '}
              <a href={`mailto:${data.disclosure.contact_email}`} className="underline underline-offset-4">
                {data.disclosure.contact_email}
              </a>
              . Our full policy, including safe harbour and what is in scope, is on the{' '}
              <Link to="/trust/vulnerability-disclosure" className="underline underline-offset-4">
                vulnerability disclosure page
              </Link>
              .
            </p>
          </Section>

          {/* Item 9 — real empty state. */}
          <Section
            id="incidents"
            title="Incident history"
            description="Incidents we have published, most recent first."
          >
            <IncidentList incidents={data.incidents} />
          </Section>
        </>
      ) : null}
    </div>
  )
}

export default TrustCentrePage
